import { InputHTMLAttributes, forwardRef, ReactNode, useId } from 'react';

export type ToggleSize = 'sm' | 'md';

export interface ToggleProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  label?: ReactNode;
  description?: ReactNode;
  size?: ToggleSize;
}

const TRACK_SIZE: Record<ToggleSize, string> = {
  sm: 'w-8 h-4',
  md: 'w-10 h-5',
};

const KNOB_SIZE: Record<ToggleSize, string> = {
  sm: 'w-3 h-3 left-0.5',
  md: 'w-4 h-4 left-0.5',
};

const KNOB_TRANSLATE: Record<ToggleSize, string> = {
  sm: 'peer-checked:translate-x-4',
  md: 'peer-checked:translate-x-5',
};

/**
 * iOS-style pill switch. Natywny checkbox z wizualnym track+knob pod spodem.
 */
export const Toggle = forwardRef<HTMLInputElement, ToggleProps>(function Toggle(
  { label, description, size = 'md', className, disabled, id: idProp, ...rest },
  ref,
) {
  const autoId = useId();
  const id = idProp ?? autoId;
  const wrapperDisabled = disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer';

  return (
    <label
      htmlFor={id}
      className={['inline-flex items-start gap-4', wrapperDisabled, className].filter(Boolean).join(' ')}
    >
      <span className={['relative inline-block shrink-0', TRACK_SIZE[size]].join(' ')}>
        <input
          ref={ref}
          type="checkbox"
          id={id}
          disabled={disabled}
          className="peer sr-only"
          {...rest}
        />
        {/* Track */}
        <span
          className={[
            'absolute inset-0 rounded-full bg-surface-muted border border-border transition-colors duration-fast ease-smooth',
            'peer-checked:bg-accent-primary peer-checked:border-accent-primary',
            'peer-focus-visible:ring-2 peer-focus-visible:ring-accent-primary/40 peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-surface-base',
          ].join(' ')}
          aria-hidden="true"
        />
        {/* Knob */}
        <span
          className={[
            KNOB_SIZE[size],
            KNOB_TRANSLATE[size],
            'absolute top-1/2 -translate-y-1/2 rounded-full bg-white shadow-sm transition-transform duration-fast ease-smooth',
          ].join(' ')}
          aria-hidden="true"
        />
      </span>
      {(label || description) && (
        <span className="flex flex-col min-w-0 -mt-0.5">
          {label && <span className="text-sm text-text-primary leading-tight select-none">{label}</span>}
          {description && <span className="text-xs text-text-muted leading-snug mt-0.5">{description}</span>}
        </span>
      )}
    </label>
  );
});
